import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useContentProtection } from '../../hooks/useContentProtection';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../hooks/use-toast';
import {
  Mic,
  MicOff,
  ChevronRight,
  Trophy,
  Target,
  AlertCircle,
  Clock,
  Home,
  Award,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Lightbulb,
  BookOpen
} from 'lucide-react';

const ARCFinalExam = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [eligible, setEligible] = useState(false);
  const [notEligibleReason, setNotEligibleReason] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [examStartTime, setExamStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [examResult, setExamResult] = useState(null);
  useProctoredAttempt({ active: Boolean(exam && examStartTime) && !showResults, label: 'final exam', onForceEnd: () => completeExam() });
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const examTimerRef = useRef(null);

  const fetchExam = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/arc/final-exam');
      
      if (!response.data.eligible) {
        setEligible(false);
        setNotEligibleReason(response.data.reason);
      } else {
        setEligible(true);
        setExam(response.data);
        setExamStartTime(Date.now());
        setTimeRemaining(response.data.exam.time_limit_minutes * 60);
      }
    } catch (error) {
      console.error('Failed to fetch exam:', error);
      toast({
        title: 'Error',
        description: 'Failed to load exam',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    fetchExam();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (examTimerRef.current) clearInterval(examTimerRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [fetchExam]);

  // Exam timer
  useEffect(() => {
    if (examStartTime && timeRemaining > 0 && !showResults) {
      examTimerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(examTimerRef.current);
            // Auto-complete exam when time runs out
            completeExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(examTimerRef.current);
    }
  }, [examStartTime, showResults]);

  const formatExamTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone error:', error);
      toast({
        title: 'Microphone Error',
        description: 'Please allow microphone access.',
        variant: 'destructive'
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const submitAnswer = async () => {
    if (!audioBlob || recordingTime < 3) {
      toast({
        title: 'Recording Too Short',
        description: 'Please record at least 3 seconds.',
        variant: 'destructive'
      });
      return;
    }
    
    const currentQuestion = exam.questions[currentQuestionIndex];
    
    try {
      setSubmitting(true);
      
      // Transcribe
      const formData = new FormData();
      formData.append('audio', audioBlob, 'answer.webm');
      
      const transcribeResponse = await api.post('/arc/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const transcript = transcribeResponse.data.transcript;
      
      // Submit for evaluation
      const evalResponse = await api.post('/arc/final-exam/answer', {
        question_id: currentQuestion.id,
        audio_transcript: transcript
      });
      
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: {
          transcript,
          ...evalResponse.data
        }
      }));
      
      // Move to next or complete
      if (currentQuestionIndex < exam.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
        setAudioBlob(null);
        setRecordingTime(0);
      } else {
        await completeExam();
      }
      
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: 'Submission Error',
        description: 'Failed to submit answer.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const completeExam = async () => {
    try {
      const response = await api.post('/arc/final-exam/complete');
      setExamResult(response.data);
      setShowResults(true);
      if (examTimerRef.current) clearInterval(examTimerRef.current);
    } catch (error) {
      console.error('Complete exam error:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete exam',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-[60vh]" />
        </div>
      </MainLayout>
    );
  }

  // Not eligible
  if (!eligible) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md text-center p-8">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-2xl font-bold mb-2">Not Yet Eligible</h2>
            <p className="text-muted-foreground mb-6">{notEligibleReason}</p>
            <Button onClick={() => navigate('/arc')}>
              <BookOpen className="h-4 w-4 mr-2" />
              Continue Learning
            </Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Show Results
  if (showResults && examResult) {
    return (
      <MainLayout>
        <div className="p-6 max-w-3xl mx-auto space-y-6">
          <Card className={`${examResult.passed ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-950/30 dark:via-yellow-950/30 dark:to-orange-950/30 border-amber-300' : 'bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/30 dark:to-gray-950/30'}`}>
            <CardContent className="pt-10 pb-10 text-center">
              {examResult.passed ? (
                <>
                  <div className="relative inline-block mb-6">
                    <Award className="h-24 w-24 text-amber-500" />
                    <div className="absolute -top-2 -right-2 bg-emerald-500 rounded-full p-2">
                      <CheckCircle2 className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    Congratulations!
                  </h1>
                  <p className="text-xl mb-2">You are now ARC Certified!</p>
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-2 mb-6">
                    Certificate ID: {examResult.certificate_id}
                  </Badge>
                </>
              ) : (
                <>
                  <Target className="h-20 w-20 mx-auto mb-4 text-slate-400" />
                  <h1 className="text-3xl font-bold mb-2">Almost There!</h1>
                  <p className="text-muted-foreground mb-4">{examResult.message}</p>
                </>
              )}
              
              <div className="flex justify-center gap-12 mt-6">
                <div>
                  <p className="text-5xl font-bold">{Math.round(examResult.score)}%</p>
                  <p className="text-sm text-muted-foreground">Your Score</p>
                </div>
                <div>
                  <p className="text-5xl font-bold">{examResult.passing_score}%</p>
                  <p className="text-sm text-muted-foreground">Required</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Improvement Areas */}
          {examResult.improvement_areas && examResult.improvement_areas.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  Areas for Improvement
                </CardTitle>
                <CardDescription>
                  Focus on these areas to strengthen your recruiting knowledge
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {examResult.improvement_areas.map((area, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Target className="h-4 w-4 text-amber-500 mt-0.5" />
                      {area}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-center gap-4">
            {examResult.can_retry && (
              <Button variant="outline" onClick={() => window.location.reload()}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Retry Exam
              </Button>
            )}
            <Button 
              onClick={() => navigate('/arc')}
              className={examResult.passed ? 'bg-gradient-to-r from-amber-500 to-orange-600' : ''}
            >
              <Home className="h-4 w-4 mr-2" />
              {examResult.passed ? 'View Certificate' : 'Back to Course'}
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const currentQuestion = exam?.questions[currentQuestionIndex];

  return (
    <MainLayout>
      <div 
        className="p-6 max-w-3xl mx-auto space-y-6 select-none"
        style={{ userSelect: 'none' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Header with Timer */}
        <div className="flex items-center justify-between">
          <div>
            <Badge variant="outline" className="mb-2">Final Certification Exam</Badge>
            <h1 className="text-xl font-bold">ARC Final Assessment</h1>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-mono font-bold ${timeRemaining < 300 ? 'text-destructive animate-pulse' : ''}`}>
              <Clock className="h-5 w-5 inline mr-2" />
              {formatExamTime(timeRemaining)}
            </div>
            <p className="text-xs text-muted-foreground">Time Remaining</p>
          </div>
        </div>

        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Question {currentQuestionIndex + 1} of {exam.questions.length}</span>
            <span>{Math.round(((currentQuestionIndex) / exam.questions.length) * 100)}% Complete</span>
          </div>
          <Progress value={(currentQuestionIndex / exam.questions.length) * 100} className="h-2" />
        </div>

        {/* Question */}
        <Card className="min-h-[50vh]">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="secondary" className="mb-2">{currentQuestion.module_title}</Badge>
                <CardTitle className="text-lg">Question {currentQuestionIndex + 1}</CardTitle>
              </div>
              <Badge>{currentQuestion.chapter_title}</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-xl font-medium mb-8">{currentQuestion.question}</p>

            {/* Recording */}
            <div className="text-center space-y-4">
              {!isRecording && !audioBlob && (
                <Button
                  size="lg"
                  onClick={startRecording}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 h-20 w-20 rounded-full"
                >
                  <Mic className="h-8 w-8" />
                </Button>
              )}
              
              {isRecording && (
                <>
                  <Button
                    size="lg"
                    variant="destructive"
                    onClick={stopRecording}
                    className="h-20 w-20 rounded-full animate-pulse"
                  >
                    <MicOff className="h-8 w-8" />
                  </Button>
                  <div className="text-2xl font-mono font-bold text-destructive">
                    {formatExamTime(recordingTime)}
                  </div>
                </>
              )}
              
              {audioBlob && !isRecording && (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4 p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                    <span>Recording: {formatExamTime(recordingTime)}</span>
                  </div>
                  
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" onClick={() => { setAudioBlob(null); setRecordingTime(0); }}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Re-record
                    </Button>
                    <Button 
                      onClick={submitAnswer}
                      disabled={submitting}
                      className="bg-gradient-to-r from-amber-500 to-orange-600"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                          Evaluating...
                        </>
                      ) : currentQuestionIndex < exam.questions.length - 1 ? (
                        <>
                          Next Question
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </>
                      ) : (
                        <>
                          <Trophy className="h-4 w-4 mr-2" />
                          Complete Exam
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Warning */}
        {timeRemaining < 600 && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-center">
            <AlertCircle className="h-4 w-4 inline mr-2 text-destructive" />
            <span className="text-sm text-destructive">
              Less than {Math.ceil(timeRemaining / 60)} minutes remaining!
            </span>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ARCFinalExam;
